import { Popover, Text, Stack, Loader, Badge, ActionIcon, Group, Box } from '@mantine/core';
import { IconVolume } from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { getWordDefinition, getAudioUrl, type WordDefinition } from '@/services/dictionaryService';
import { flashcardApi } from '@/features/flashcards/api/flashcardApi';

interface DictionaryPopoverProps {
  word: string;
  opened: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  storyId?: string;
  /** When set, backend credits this lookup to the given student's vocab deck
   *  instead of the caller's. Used by the parent-reading-with-kid flow. */
  creditToStudentId?: string | null;
}

export function DictionaryPopover({ word, opened, position, onClose, storyId, creditToStudentId }: DictionaryPopoverProps) {
  const [definition, setDefinition] = useState<WordDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!opened || !word) {
      setDefinition(null);
      setError(null);
      return;
    }

    const fetchDefinition = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await getWordDefinition(word);
        setDefinition(result);

        // Track the lookup in the backend for vocabulary deck building
        if (storyId) {
          flashcardApi.lookupWord(word, storyId, creditToStudentId ?? undefined).catch(() => {
            // Fire-and-forget: don't block UI if tracking fails
          });
        }
      } catch (err) {
        console.error('Failed to fetch definition:', err);
        setError('Definition not found');
      } finally {
        setLoading(false);
      }
    };

    fetchDefinition();
  }, [word, opened]);

  const playAudio = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (definition?.audioFile) {
      const audioUrl = getAudioUrl(definition.audioFile);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play().catch(err => {
          console.error('Failed to play audio:', err);
        });
      } else {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.play().catch(err => {
          console.error('Failed to play audio:', err);
        });
      }
    }
  };

  if (!opened) return null;

  return (
    <Popover
      opened={opened}
      onChange={onClose}
      position="bottom-start"
      withArrow
      shadow="md"
      width={300}
      middlewares={{ flip: true, shift: true }}
      withinPortal
      closeOnClickOutside
      closeOnEscape
    >
      <Popover.Target>
        <Box
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            width: 1,
            height: 1,
            pointerEvents: 'none',
          }}
        />
      </Popover.Target>
      <Popover.Dropdown onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <Stack align="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Looking up definition...
            </Text>
          </Stack>
        ) : error ? (
          <Stack gap="xs">
            <Text size="sm" c="red">
              {error}
            </Text>
            <Text size="xs" c="dimmed">
              Word: {word}
            </Text>
          </Stack>
        ) : definition ? (
          <Stack gap="sm">
            <Stack gap={4}>
              <Group justify="space-between" align="center" wrap="nowrap">
                <Text fw={700} size="lg">
                  {definition.word}
                </Text>
                {definition.audioFile && (
                  <ActionIcon
                    variant="light"
                    color="blue"
                    size="md"
                    onClick={playAudio}
                    aria-label="Play pronunciation"
                  >
                    <IconVolume size={18} />
                  </ActionIcon>
                )}
              </Group>
              {definition.partOfSpeech && (
                <Badge size="sm" variant="light" color="blue">
                  {definition.partOfSpeech}
                </Badge>
              )}
              {definition.pronunciations && definition.pronunciations.length > 0 && (
                <Text size="xs" c="dimmed">
                  {definition.pronunciations[0]}
                </Text>
              )}
            </Stack>
            <Text size="sm">{definition.definition}</Text>
          </Stack>
        ) : null}
      </Popover.Dropdown>
    </Popover>
  );
}
